/*
 * Zone state — which area of the Fernhollow world the player is currently in.
 *
 * v0.4 scope: visual-only zone swap. The PartyKit room stays the same when the
 * player walks to the porch; this means players in the apothecary still see each
 * other as if they were in the square (and vice versa). Treat this as the MVP
 * for TASK-07 — the spec'd "separate PartyKit room per zone" is a follow-up.
 *
 * The current zone persists in its own localStorage key (NOT extending SaveData)
 * so a refresh returns the player to the same zone they were in.
 */

const STORAGE_KEY = 'fernhollow-zone';

export type ZoneId = 'square' | 'apothecary';

export const ZONES: ReadonlyArray<ZoneId> = ['square', 'apothecary'];

let currentZone: ZoneId = loadZone();

function loadZone(): ZoneId {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'apothecary' || raw === 'square') return raw;
  } catch {
    /* fall through */
  }
  return 'square';
}

export function getCurrentZone(): ZoneId {
  return currentZone;
}

export function setCurrentZone(zone: ZoneId): void {
  if (zone === currentZone) return;
  currentZone = zone;
  try {
    window.localStorage.setItem(STORAGE_KEY, zone);
  } catch {
    /* swallow — zone isn't critical enough to surface */
  }
}

/** Where the player spawns when entering a zone (world coords). */
export function spawnPositionFor(zone: ZoneId): { x: number; y: number } {
  switch (zone) {
    case 'square':
      // Just north of the portal so the player isn't immediately re-prompted.
      return { x: 300, y: 700 };
    case 'apothecary':
      // North side of the porch — opposite end from the return portal.
      return { x: 300, y: 200 };
  }
}

/** Portal hotspot per zone — where the proximity prompt fires. */
export interface Portal {
  /** Hotspot world coord. */
  x: number;
  y: number;
  /** Detection radius. */
  radius: number;
  /** Player-facing prompt label. */
  label: string;
  /** Zone the player travels TO when they tap this portal. */
  toZone: ZoneId;
}

export function portalFor(zone: ZoneId): Portal {
  switch (zone) {
    case 'square':
      return {
        x: 300,
        y: 750,
        radius: 70,
        label: '🌿 Walk to the porch',
        toZone: 'apothecary',
      };
    case 'apothecary':
      return {
        x: 300,
        y: 750,
        radius: 70,
        label: '🌿 Back to the square',
        toZone: 'square',
      };
  }
}
